import { Button, IconButton } from '@ui/shared/Button';
import isEqual from 'lodash/isEqual';
import reject from 'lodash/reject';
import uniqBy from 'lodash/uniqBy';
import * as React from 'react';
import { IComparisonReportSettings } from '../../../../data/providers/settings/reports/comparisonReportSettingsProvider';
import {
  ICrossTestMetricSelection,
  ISelectableMetric,
  ISelectableTestType,
} from '../../../../data/types';
import { ScrollableArea } from '../../../layout/ScrollableArea';
import { CardHeader, CardHeaderLeft } from '../../../shared/card/CardHeader';
import { Count } from '../../../shared/Count';
import { ControlLabel } from '../../../shared/form/ControlLabel';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { ListBulkOperation } from '../../../shared/form/ListBulkOperation';
import { ListControlLabel } from '../../../shared/form/ListControlLabel';
import { SingleMetricSelect } from '../../../shared/form/selects/SingleMetricSelect';
import { TestTypeSelect } from '../../../shared/form/selects/TestTypeSelect';
import { Step } from '../../../shared/form/Step';
import { ListControlContainer } from '../../../shared/List/ListControlContainer';
import { FormSection } from '../../utils/settings/FormSection';
import { MetricList } from '../../utils/settings/MetricList';

interface IProps {
  settings: IComparisonReportSettings;
  onSettingsChange: (settings: IComparisonReportSettings) => void;
  startingStep: number;
}

export const MetricForm = (props: IProps): React.ReactElement => {
  const [metric, setMetric] = React.useState<ISelectableMetric>();
  const metrics = React.useMemo((): ISelectableMetric[] => {
    return props.settings?.metrics?.[props?.settings?.testType?.value] ?? [];
  }, [
    JSON.stringify(props.settings?.metrics),
    props?.settings?.testType?.value,
  ]);

  /**
   * @description handles user selecting a metric
   * @param metricSelection
   */
  const handleMetricSelection = (metricSelection: ISelectableMetric): void => {
    /// prevent adding duplicate metrics
    const mMetrics = [...metrics, metricSelection];
    const newMetrics = uniqBy(
      mMetrics,
      (selection: ISelectableMetric): string => selection.value
    );
    props.onSettingsChange({
      ...props.settings,
      metrics: {
        ...props.settings?.metrics,
        [props.settings?.testType?.value]: newMetrics,
      },
    });
  };

  /**
   * @description handles removing a metric
   * @param metricSelection
   */
  const handleRemoveMetricSelection = (
    metricSelection: ISelectableMetric | ICrossTestMetricSelection
  ): void => {
    const newMetrics = reject(
      metrics,
      (selection: ISelectableMetric): boolean => {
        return isEqual(selection, metricSelection);
      }
    );
    props.onSettingsChange({
      ...props.settings,
      metrics: {
        ...props.settings?.metrics,
        [props.settings?.testType?.value]: newMetrics,
      },
    });
  };

  /**
   * @description handles change to test type
   * @param testType
   */
  const testTypeChangeHandler = (
    testType: ISelectableTestType | ISelectableTestType[]
  ): void => {
    setMetric(null);
    props.onSettingsChange({
      ...props.settings,
      testType: testType as ISelectableTestType,
    });
  };

  /**
   * @description adds metric
   * @returns
   */
  const addMetric = (): void => handleMetricSelection(metric);

  /**
   * @description clears the current metrics
   */
  const clearMetricsHandler = (): void => {
    props.onSettingsChange({
      ...props.settings,
      metrics: {
        ...props.settings?.metrics,
        [props?.settings?.testType?.value]: [],
      },
    });
  };

  const reorderHandler = (
    mMetrics: Array<ISelectableMetric | ICrossTestMetricSelection>
  ): void => {
    props.onSettingsChange({
      ...props.settings,
      metrics: {
        ...props.settings?.metrics,
        [props?.settings?.testType?.value]: mMetrics as ISelectableMetric[],
      },
    });
  };

  return (
    <React.Fragment>
      <FormSection>
        <CardHeader>
          <CardHeaderLeft>
            <Step>{props.startingStep + 1}</Step>
            <h6>Choose Test Type & Metrics</h6>
          </CardHeaderLeft>
        </CardHeader>
        <div style={{ padding: '10px', height: '100%' }}>
          <ControlSpacer>
            <ControlLabel>Test Type</ControlLabel>
            <TestTypeSelect
              value={props.settings?.testType}
              onChange={testTypeChangeHandler}
              disabled={false}
            />
          </ControlSpacer>
          <ControlSpacer>
            <ControlLabel>Metric</ControlLabel>
            <SingleMetricSelect
              testTypeId={props.settings?.testType?.value}
              disabled={!props.settings?.testType?.value}
              value={metric}
              onChange={setMetric}
            />
          </ControlSpacer>
          <ControlSpacer>
            <Button
              icon='health_metrics'
              title='Add Metric'
              onClick={addMetric}
              disabled={!props.settings?.testType?.value || !metric}
            />
          </ControlSpacer>
          <ListControlLabel label='Metrics'>
            <IconButton
              icon='cancel'
              title='Clear All'
              onClick={clearMetricsHandler}
              disabled={metrics.length === 0}
            />
            <Count value={metrics?.length} />
          </ListControlLabel>
          <ListControlContainer>
            <ScrollableArea
              style={{
                flex: 1,
              }}
            >
              {metrics?.length > 0 && (
                <MetricList
                  metrics={metrics as ISelectableMetric[]}
                  onRemoveSelection={handleRemoveMetricSelection}
                  onReorder={reorderHandler}
                />
              )}
            </ScrollableArea>
          </ListControlContainer>
        </div>
      </FormSection>
    </React.Fragment>
  );
};
